import { router } from "@inertiajs/react"
import type { DrawerProp } from "@/types/v2/drawer"
import { applyPreviewUpdate } from "@/lib/preview-prop-registry"

// The contact payload powering the v2 inline-edit hook
// lives under different prop names depending on the host
// page:
//
// - `"contact"` — top-level prop on the contact show page
//   (full-page `pages/v2/contacts/show.tsx`).
// - `"drawer"` — the unified slideover prop. The contact
//   data lives nested at `drawer.active.data` when the
//   active trail entry is a contact.
// - `"preview"` — the command-palette rich-preview pane
//   (CON-183). No Inertia `drawer` prop is mounted there;
//   the contact data lives in the pane's local state, so the
//   optimistic transform routes through the preview-prop
//   registry instead of `router.replaceProp`.
//
// Hooks call `replaceContactProp(propKey, transformer)` and
// don't need to know which shape the host page uses. This
// mirrors `replaceDealProp` so both inline-edit hooks have
// the same "host-agnostic optimistic patch" contract.
//
// The transformer takes and returns a generic record. The
// show page's `ContactShowData` and the drawer's
// `ContactDrawerData` overlap on the inline-edit fields
// shipped by `V2::ContactDetailSerializer` (the only ones
// callers patch), so this signature is permissive on
// purpose — see `useContactUpdate` for the actual call
// shapes.

export type ContactPropKey = "contact" | "drawer" | "preview"

type ContactRecord = Record<string, unknown>

export function replaceContactProp(
  propKey: ContactPropKey,
  transformer: (current: ContactRecord) => ContactRecord,
) {
  if (propKey === "preview") {
    applyPreviewUpdate((d) => transformer(d as ContactRecord) as unknown)
    return
  }

  if (propKey === "drawer") {
    router.replaceProp("drawer", (prev: DrawerProp | null) => {
      if (!prev?.active || prev.active.type !== "contact") return prev
      const updated = transformer(
        prev.active.data as unknown as ContactRecord,
      )
      return {
        ...prev,
        active: {
          ...prev.active,
          data: updated as unknown as typeof prev.active.data,
        },
      }
    })
    return
  }

  router.replaceProp(propKey, (prev: unknown) => {
    if (!prev || typeof prev !== "object") return prev
    return transformer(prev as ContactRecord)
  })
}
